import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { Search, Plus, AlertTriangle, Clock, ShieldCheck, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { formatDate, daysUntil, formatApiError } from "../lib/format";
import { TASK_STATUSES, TASK_STATUS_LABEL, TASK_PRIORITY_LABEL, TASK_PRIORITIES, DIVISIONS } from "../lib/constants";
import TaskDetailModal from "../components/TaskDetailModal";

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [fstat, setFstat] = useState("open");
  const [fdiv, setFdiv] = useState("all");
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState({ title: "", due_date: new Date().toISOString().slice(0,10), priority: "Normal", status: "To Do", notes: "", assigned_staff: "" });

  const load = () => api.get("/tasks").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submitPersonal = async () => {
    try {
      await api.post("/tasks", { ...form, booking_id: null, division_id: null });
      toast.success("Tugas pribadi dibuat"); setOpen(false); setForm({ title: "", due_date: new Date().toISOString().slice(0,10), priority: "Normal", status: "To Do", notes: "", assigned_staff: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => rows.filter((t) => {
    if (fstat === "open" && t.status === "Done") return false;
    if (fstat === "today" && (t.due_date !== new Date().toISOString().slice(0, 10) || t.status === "Done")) return false;
    if (fstat === "overdue" && (daysUntil(t.due_date) >= 0 || t.status === "Done")) return false;
    if (fstat === "personal" && !t.is_personal) return false;
    if (fstat === "blocked" && !t.blocked) return false;
    if (fstat === "await_verify" && !(t.submitted_done && !t.verified_done)) return false;
    if (fdiv !== "all" && t.division_id !== fdiv) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!t.title.toLowerCase().includes(s) && !(t.client_name || "").toLowerCase().includes(s)) return false;
    }
    return true;
  }), [rows, fstat, fdiv, q]);

  const change = async (id, status) => {
    try { await api.put(`/tasks/${id}`, { status }); toast.success("Status tugas diperbarui"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = useMemo(() => ({
    blocked: rows.filter((t) => t.blocked).length,
    await_verify: rows.filter((t) => t.submitted_done && !t.verified_done).length,
    overdue: rows.filter((t) => daysUntil(t.due_date) < 0 && t.status !== "Done").length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Tugas & Tindakan</h1>
          <p className="text-sm text-[#5C6066] mt-1">Daftar semua tugas lintas divisi + tugas pribadi Anda.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand text-white" data-testid="btn-new-personal-task"><Plus className="w-4 h-4 mr-1.5"/> Tugas Pribadi</Button></DialogTrigger>
          <DialogContent data-testid="modal-personal-task">
            <DialogHeader><DialogTitle>Tugas Pribadi Baru</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Judul</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="personal-task-title"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Jatuh Tempo</Label><Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}/></div>
                <div><Label>Prioritas</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>{TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-brand text-white" onClick={submitPersonal} disabled={!form.title} data-testid="btn-save-personal-task">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick counters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button onClick={() => setFstat("await_verify")} className={`text-left border rounded-md p-3 bg-white hover:bg-[#FAF6EE] transition ${fstat==="await_verify" ? "border-amber-400 ring-1 ring-amber-300" : "border-[#E5E0D8]"}`} data-testid="counter-await-verify">
          <div className="flex items-center gap-2 text-amber-700"><Clock className="w-4 h-4"/><span className="text-[11px] uppercase font-semibold">Menunggu Verifikasi</span></div>
          <div className="text-2xl font-bold">{counts.await_verify}</div>
        </button>
        <button onClick={() => setFstat("blocked")} className={`text-left border rounded-md p-3 bg-white hover:bg-[#FAF6EE] transition ${fstat==="blocked" ? "border-rose-400 ring-1 ring-rose-300" : "border-[#E5E0D8]"}`} data-testid="counter-blocked">
          <div className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-4 h-4"/><span className="text-[11px] uppercase font-semibold">Lapor Kendala</span></div>
          <div className="text-2xl font-bold">{counts.blocked}</div>
        </button>
        <button onClick={() => setFstat("overdue")} className={`text-left border rounded-md p-3 bg-white hover:bg-[#FAF6EE] transition ${fstat==="overdue" ? "border-orange-400 ring-1 ring-orange-300" : "border-[#E5E0D8]"}`} data-testid="counter-overdue">
          <div className="flex items-center gap-2 text-orange-700"><AlertTriangle className="w-4 h-4"/><span className="text-[11px] uppercase font-semibold">Terlambat</span></div>
          <div className="text-2xl font-bold">{counts.overdue}</div>
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari tugas atau klien..." value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white" data-testid="tasks-search" />
        </div>
        <Select value={fstat} onValueChange={setFstat}>
          <SelectTrigger className="w-52 bg-white" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="open">Belum Selesai</SelectItem>
            <SelectItem value="today">Hari Ini</SelectItem>
            <SelectItem value="overdue">Terlambat</SelectItem>
            <SelectItem value="await_verify">Menunggu Verifikasi</SelectItem>
            <SelectItem value="blocked">Ada Kendala</SelectItem>
            <SelectItem value="personal">Pribadi Saya</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fdiv} onValueChange={setFdiv}>
          <SelectTrigger className="w-52 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Divisi</SelectItem>
            {DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Tugas</th>
                <th className="text-left px-4 py-3">Klien / Konteks</th>
                <th className="text-left px-4 py-3">Divisi</th>
                <th className="text-left px-4 py-3">Progres</th>
                <th className="text-left px-4 py-3">Jatuh Tempo</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8C9096]">Tidak ada tugas.</td></tr>}
              {filtered.map((t) => {
                const d = daysUntil(t.due_date);
                const late = d < 0 && t.status !== "Done";
                const div = DIVISIONS.find((x) => x.id === t.division_id);
                const cl = t.checklist || [];
                const doneCount = cl.filter((x) => x.done).length;
                const pct = cl.length ? Math.round((100 * doneCount) / cl.length) : (t.progress_pct || 0);
                return (
                  <tr key={t.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`task-row-${t.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        {t.title}
                        {t.is_personal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">PRIBADI</span>}
                        {t.blocked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 inline-flex items-center gap-0.5" title={t.blocker_note}><AlertTriangle className="w-3 h-3"/>KENDALA</span>}
                        {t.submitted_done && !t.verified_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-0.5"><Clock className="w-3 h-3"/>VERIF</span>}
                        {t.verified_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-0.5"><ShieldCheck className="w-3 h-3"/>OK</span>}
                      </div>
                      <div className="text-[11px] text-[#8C9096]">{t.assigned_staff || "Belum ditugaskan"} • <StatusBadge status={t.priority} label={TASK_PRIORITY_LABEL[t.priority]} /></div>
                    </td>
                    <td className="px-4 py-3">
                      {t.is_personal ? <span className="text-xs text-[#8C9096] italic">Tugas pribadi</span> : (
                        <>
                          <Link to={`/bookings/${t.booking_id}`} className="text-brand hover:underline text-sm">{t.client_name || "-"}</Link>
                          <div className="text-[11px] text-[#8C9096] font-mono">{t.booking_code}</div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">{div ? <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${div.color}`}>{div.short}</span> : (t.is_personal ? "—" : "-")}</td>
                    <td className="px-4 py-3 min-w-[110px]">
                      {cl.length > 0 ? (
                        <div>
                          <div className="w-full h-1.5 bg-[#F0EBE0] rounded overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                          <div className="text-[10px] text-[#8C9096] mt-0.5">{doneCount}/{cl.length}</div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#8C9096] italic">—</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 ${late ? "text-rose-700 font-medium" : ""}`}>
                      {formatDate(t.due_date)}
                      {t.status !== "Done" && <div className="text-[11px]">{d >= 0 ? `${d} hari lagi` : `Terlambat ${Math.abs(d)} hari`}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Select value={t.status} onValueChange={(v) => change(t.id, v)}>
                        <SelectTrigger className="h-8 w-36 text-xs bg-white" data-testid={`task-status-select-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABEL[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setDetailId(t.id)} data-testid={`btn-open-task-${t.id}`}>
                        <FolderOpen className="w-3.5 h-3.5 mr-1"/>Buka
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <TaskDetailModal
        taskId={detailId}
        open={!!detailId}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
        onChanged={load}
      />
    </div>
  );
}
